import initSqlJs from 'sql.js';
import {
  PairingController,
  PlayerApiClient,
  PlayerRuntime,
  systemClock,
  type PlaybackPlan,
  type RuntimeStatus,
} from '@signage/player-core';
import { startPlaybackSurface } from '@signage/player-ui';
import { installBridge } from '@signage/player-ui/host/bridge';
import { WasmSqliteDatabase } from '@signage/player-ui/host/database';
import { FetchHttpAdapter } from '@signage/player-ui/host/http';
import { startWatchdog } from '@signage/player-ui/host/watchdog';
import { WebFileSystem } from './filesystem.js';
import { WebLogger } from './logger.js';

const PLAYER_VERSION = '0.4.2';

/**
 * Web player entry point: the same runtime as the Windows and Android players, in a browser.
 *
 * Made for screens that can open a web page full screen at power-on: Samsung Smart Signage
 * (URL Launcher), LG webOS Signage, and any computer or kiosk with a browser. Content, the
 * schedule and the pairing all live in the browser's storage, and a service worker keeps the
 * player and its media available, so it keeps playing when the internet drops.
 */

// Installed before anything is awaited, because the playback surface subscribes on start.
const bridge = installBridge();

/** The player's own address; media is served by the service worker under it. */
const SCOPE = new URL('./', location.href).toString();

async function main(): Promise<void> {
  const fs = new WebFileSystem();
  const logger = new WebLogger();

  await registerServiceWorker(logger);
  // Ask the browser not to clear the cache to free space; a signage screen depends on it.
  void navigator.storage?.persist?.().then((granted) => logger.info('Persistent storage', { granted })).catch(() => {});

  const mediaUrlPrefix = `${SCOPE}__media__/`;
  bridge.configure({ logger, mediaUrl: (sha256: string) => `${mediaUrlPrefix}${sha256}`, mediaUrlPrefix });
  startPlaybackSurface();

  const SQL = await initSqlJs({ locateFile: (file: string) => `${SCOPE}${file}` });
  const database = await WasmSqliteDatabase.open(SQL, fs, 'player.db', (err) => {
    logger.error('Local database was unreadable and has been rebuilt', { error: err.message });
  });

  onNewVersion = async () => {
    await database.flush();
    window.location.reload();
  };

  const storedBaseUrl =serverFromUrl() ?? database.getSetting('apiBaseUrl') ?? defaultServer();
  const api = new PlayerApiClient(new FetchHttpAdapter(), storedBaseUrl, database.getSetting('deviceToken'), logger);

  const descriptor = {
    platform: 'web',
    osVersion: browserName(),
    model: navigator.platform || 'Browser',
    arch: 'web',
    playerVersion: PLAYER_VERSION,
    hardwareId: hardwareId(database),
    width: Math.round(screenSize().width * (window.devicePixelRatio || 1)),
    height: Math.round(screenSize().height * (window.devicePixelRatio || 1)),
    orientation: screenSize().width >= screenSize().height ? ('landscape' as const) : ('portrait' as const),
  };
  logger.info('Player starting', { version: PLAYER_VERSION, browser: descriptor.osVersion, server: storedBaseUrl });

  const runtime = new PlayerRuntime(
    fs,
    database,
    api,
    systemClock,
    logger,
    { device: descriptor, storage: () => fs.diskSpace() },
    {
      onPlanChanged: (plan: PlaybackPlan) => bridge.emitPlan(plan),
      onStatusChanged: (status: RuntimeStatus) => bridge.emitStatus(status),
      onCommand: (command) => handleCommand(command.command, command.payload),
      onUnpaired: () => {
        logger.warn('Device was unpaired by the server');
        void pairing.start();
      },
    },
    'media',
  );

  const pairing = new PairingController(api, runtime, database, logger, {
    onUpdate: (state) => bridge.emitPairing(state),
    device: descriptor,
    defaultBaseUrl: storedBaseUrl,
  });

  bridge.attach(runtime, pairing, database);

  async function handleCommand(command: string, payload?: Record<string, unknown> | null): Promise<'done' | 'failed'> {
    try {
      switch (command) {
        case 'reload':
        case 'restart':
          // A browser player cannot restart its process; reloading the page is the equivalent.
          await database.flush();
          await logger.save();
          window.location.reload();
          return 'done';
        case 'identify':
          bridge.emitIdentify(Number(payload?.durationMs ?? 10_000) || 10_000);
          return 'done';
        case 'screenshot':
          return (await bridge.captureScreenshot(api)) ? 'done' : 'failed';
        default:
          logger.warn('Unknown command from server', { command });
          return 'failed';
      }
    } catch (err) {
      logger.error('Command handling failed', { command, error: (err as Error).message });
      return 'failed';
    }
  }

  keepAwake(logger);
  enterFullscreenOnFirstInput();

  await runtime.start();
  if (!database.getSetting('deviceToken')) await pairing.start();

  startWatchdog({
    logger,
    isHealthy: () => bridge.lastHeartbeatAge() < 60_000,
    onUnhealthy: async () => {
      logger.error('Playback surface stopped responding; reloading');
      await database.flush();
      window.location.reload();
    },
  });

  // Browsers may discard a hidden page without warning, so commit when the page is hidden.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void database.flush();
      void logger.save();
    }
  });
  window.addEventListener('pagehide', () => void database.flush());
}

async function registerServiceWorker(logger: WebLogger): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('This browser cannot run the player offline (no service workers). Open it over https.');
  }
  await navigator.serviceWorker.register(`${SCOPE}sw.js`, { scope: SCOPE });
  await navigator.serviceWorker.ready;
  // The first visit installs the worker; until it controls the page, cached media cannot be
  // served. One reload hands the page to it.
  if (!navigator.serviceWorker.controller) {
    logger.info('Service worker installed; reloading so it can serve media');
    await new Promise<void>((resolve) => {
      navigator.serviceWorker.addEventListener('controllerchange', () => resolve(), { once: true });
      setTimeout(resolve, 3000);
    });
    if (!navigator.serviceWorker.controller) window.location.reload();
    return;
  }
  // A new release was published: its worker takes over and this page still runs the old code.
  // Reload into the new version (the cached content keeps playing straight after).
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    logger.info('New player version installed; reloading');
    void onNewVersion();
  });
  // Displays run for weeks without navigating, so look for a new release every hour.
  const registration = await navigator.serviceWorker.getRegistration(SCOPE);
  setInterval(() => void registration?.update().catch(() => {}), 60 * 60 * 1000);
}

/** Set once the database exists, so a version reload commits it first. */
let onNewVersion: () => Promise<void> = async () => window.location.reload();

/** Screens must not dim or sleep while playing. */
function keepAwake(logger: WebLogger): void {
  const wakeLock = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<unknown> } }).wakeLock;
  if (!wakeLock) return;
  const request = () => wakeLock.request('screen').catch((err: Error) => logger.debug('Wake lock unavailable', { error: err.message }));
  void request();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void request();
  });
}

/** Browsers only allow full screen after a click or key press; signage launchers start that way. */
function enterFullscreenOnFirstInput(): void {
  const go = () => {
    if (!document.fullscreenElement) void document.documentElement.requestFullscreen?.().catch(() => {});
  };
  window.addEventListener('pointerdown', go, { once: true });
  window.addEventListener('keydown', go, { once: true });
}

function hardwareId(database: WasmSqliteDatabase): string {
  const existing = database.getSetting('hardwareId');
  if (existing) return existing;
  const generated = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  database.setSetting('hardwareId', generated);
  return generated;
}

/**
 * The display's size. Some browsers report 0 for a hidden or embedded page; the window, then a
 * full-HD default, stand in so pairing still works.
 */
function screenSize(): { width: number; height: number } {
  const width = screen.width || window.innerWidth || 1920;
  const height = screen.height || window.innerHeight || 1080;
  return { width, height };
}

/** `?server=https://api.example.com` points a display at another server before pairing. */
function serverFromUrl(): string | null {
  const value = new URLSearchParams(location.search).get('server');
  return value && /^https?:\/\//.test(value) ? value : null;
}

/** The server this player was published from: the API is reachable under the same address. */
function defaultServer(): string {
  return location.origin;
}

function browserName(): string {
  const ua = navigator.userAgent;
  const tag = /Tizen/.test(ua) ? 'Samsung Tizen' : /Web0S|webOS/.test(ua) ? 'LG webOS' : 'Browser';
  const chrome = /Chrome\/(\d+)/.exec(ua)?.[1];
  return chrome ? `${tag} (Chromium ${chrome})` : tag;
}

/**
 * Samsung's store requires the remote's Return key to ask before leaving the app. Only the store
 * app has the `tizen` object: a Samsung signage display opening this page by URL shows no dialog.
 */
function askBeforeExitOnSamsung(): void {
  const RETURN_KEY = 10009;
  const LEFT_KEY = 37;
  const RIGHT_KEY = 39;
  const currentApp = () =>
    (window as unknown as { tizen?: { application?: { getCurrentApplication(): { exit(): void } } } }).tizen?.application?.getCurrentApplication();
  let dialog: HTMLElement | null = null;

  const close = () => {
    dialog?.remove();
    dialog = null;
  };
  const open = () => {
    const panel = document.createElement('div');
    panel.className = 'home-setup';
    panel.style.cssText = 'top:0;bottom:0;z-index:30;justify-content:center;gap:3vh;background:rgba(0,0,0,.88)';
    const question = document.createElement('p');
    question.style.cssText = 'font-size:4vh;color:#fff';
    question.textContent = 'Exit Oicas Signage?';
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:3vh';
    const stay = document.createElement('button');
    stay.textContent = 'No';
    stay.addEventListener('click', close);
    const leave = document.createElement('button');
    leave.textContent = 'Yes';
    leave.addEventListener('click', () => currentApp()?.exit());
    row.append(stay, leave);
    panel.append(question, row);
    document.body.append(panel);
    dialog = panel;
    stay.focus();
  };

  window.addEventListener('keydown', (event) => {
    if (dialog && (event.keyCode === LEFT_KEY || event.keyCode === RIGHT_KEY)) {
      const buttons = Array.from(dialog.querySelectorAll('button'));
      buttons[buttons[0] === document.activeElement ? 1 : 0]?.focus();
      return;
    }
    if (event.keyCode !== RETURN_KEY || !currentApp()) return;
    event.preventDefault();
    if (dialog) close();
    else open();
  });
}

askBeforeExitOnSamsung();

void main().catch((err: unknown) => {
  const message = err instanceof Error ? err.message : String(err);
  document.body.innerHTML = `<pre style="color:#f87171;padding:4vh;font:2vh ui-monospace,monospace;white-space:pre-wrap">Player failed to start\n\n${message}</pre>`;
  // eslint-disable-next-line no-console
  console.error('Player failed to start', err);
});
